import "./App.css";

import { Cart, CartLineItem, getCart, saveCart } from "./cart";
import { FormEvent, useMemo, useRef, useState } from "react";
import { PaymentElement, Elements, useStripe, useElements, AddressElement } from "@stripe/react-stripe-js";
import stripeJs, { loadStripe } from "@stripe/stripe-js";
import { useInventory } from "./inventory";

interface CartViewProps {
  cart: Cart;
  onChange: (newCart: Cart) => any;
}

const bagXIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-bag-x" viewBox="0 0 16 16">
    <path
      fill-rule="evenodd"
      d="M6.146 8.146a.5.5 0 0 1 .708 0L8 9.293l1.146-1.147a.5.5 0 1 1 .708.708L8.707 10l1.147 1.146a.5.5 0 0 1-.708.708L8 10.707l-1.146 1.147a.5.5 0 0 1-.708-.708L7.293 10 6.146 8.854a.5.5 0 0 1 0-.708"
    />
    <path d="M8 1a2.5 2.5 0 0 1 2.5 2.5V4h-5v-.5A2.5 2.5 0 0 1 8 1m3.5 3v-.5a3.5 3.5 0 1 0-7 0V4H1v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4zM2 5h12v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" />
  </svg>
);

const bagMinusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-bag-dash" viewBox="0 0 16 16">
    <path fill-rule="evenodd" d="M5.5 10a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 0 1H6a.5.5 0 0 1-.5-.5" />
    <path d="M8 1a2.5 2.5 0 0 1 2.5 2.5V4h-5v-.5A2.5 2.5 0 0 1 8 1m3.5 3v-.5a3.5 3.5 0 1 0-7 0V4H1v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4zM2 5h12v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" />
  </svg>
);

const bagPlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-bag-plus" viewBox="0 0 16 16">
    <path
      fill-rule="evenodd"
      d="M8 7.5a.5.5 0 0 1 .5.5v1.5H10a.5.5 0 0 1 0 1H8.5V12a.5.5 0 0 1-1 0v-1.5H6a.5.5 0 0 1 0-1h1.5V8a.5.5 0 0 1 .5-.5"
    />
    <path d="M8 1a2.5 2.5 0 0 1 2.5 2.5V4h-5v-.5A2.5 2.5 0 0 1 8 1m3.5 3v-.5a3.5 3.5 0 1 0-7 0V4H1v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4zM2 5h12v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" />
  </svg>
);

const shippingOptions: stripeJs.StripeAddressElementOptions = {
  mode: "shipping",
  fields: {
    phone: "always",
  },
  validation: {
    phone: { required: "always" },
  },
  allowedCountries: ["AU", "GB", "US"],
};

interface CheckoutLineItem {
  work_id: number;
  variant_name: string;
  qty: number;
}

interface CheckoutForm {
  line_items: CheckoutLineItem[];
  confirmationTokenId: string;
  testMode?: boolean;
  instructions: string;
  total: number;
  dryRun: boolean;
}

interface CartViewRowProps {
  lineItem: CartLineItem;
  onChange: (li: CartLineItem, delta: number) => void;
}

function CartViewRow({ lineItem: item, onChange }: CartViewRowProps) {
  const inventory = useInventory(item.work.id);

  const numAvailableForSale = useMemo(() => {
    if (!inventory) {
      return 0;
    }
    const variantInventory = inventory[item.variant.name];
    if (!variantInventory) {
      return 0;
    }
    return Math.max(0, variantInventory.available - variantInventory.purchased);
  }, [inventory, item]);

  return (
    <tr className={numAvailableForSale < item.qty ? "table-danger" : ""}>
      <td>
        <div>{item.work.name}</div>
        {item.variant.name !== "default" && <div>{item.variant.name}</div>}
      </td>
      <td>${item.variant.price}</td>
      <td>
        <button onClick={() => onChange(item, -1)} name="action" value="remove" className="btn btn-light btn-sm">
          {item.qty == 1 ? bagXIcon() : bagMinusIcon()}
        </button>
        <div style={{ display: "inline-block", textAlign: "center", width: "3em" }}>{item.qty}</div>

        <button
          onClick={() => onChange(item, 1)}
          name="action"
          disabled={item.qty >= numAvailableForSale}
          value="add"
          className="btn btn-light btn-sm"
        >
          {bagPlusIcon()}
        </button>
      </td>
      <td>${item.variant.price * item.qty}</td>
    </tr>
  );
}

function CartView({ cart, onChange }: CartViewProps) {
  if (cart.items.length === 0) {
    return (
      <div className="container-xl main">
        <div className="alert alert-warning" role="alert">
          Cart is Empty
        </div>
      </div>
    );
  }

  function changeLineItem(lineItem: CartLineItem, delta: number) {
    const newQty = lineItem.qty + delta;
    const lineItemIdx = cart.items.indexOf(lineItem);
    const newCart = {
      ...cart,
      items: [...cart.items],
    };
    if (newQty < 0) {
      return;
    } else if (newQty === 0) {
      newCart.items.splice(lineItemIdx, 1);
    } else {
      newCart.items[lineItemIdx].qty += delta;
    }
    onChange(newCart);
  }

  function changeShipping(type: "pickup" | "ship") {
    console.log("Changing shipping to", type);
    const newCart = {
      ...cart,
    };
    if (type === "pickup") {
      newCart.shipping = { type: "pickup", cost: 0 };
    } else {
      newCart.shipping = { type: "ship", cost: 15 };
    }

    onChange(newCart);
  }

  const subTotal = cart.items.reduce((accum, li) => accum + li.qty * li.variant.price, 0);
  const total = subTotal + cart.shipping.cost;

  return (
    <table className="table" style={{ marginBottom: "3em" }}>
      <thead>
        <tr>
          <th>Work</th>
          <th>Unit Price</th>
          <th>Qty</th>
          <th>Subtotal</th>
        </tr>
      </thead>
      <tbody>
        {cart.items.map((item, idx) => (
          <CartViewRow key={idx} lineItem={item} onChange={changeLineItem} />
        ))}
      </tbody>
      <tfoot className="table-group-divider">
        <tr>
          <td colSpan={3} style={{ textAlign: "right" }}>
            Subtotal
          </td>
          <td>${subTotal}</td>
        </tr>
        <tr>
          <td colSpan={4}>
            <h3>Shipping</h3>
            <div style={{ display: "flex", flexDirection: "row", gap: "1em" }}>
              <div className="form-check">
                <input
                  className="form-check-input"
                  checked={cart.shipping.type === "pickup"}
                  type="radio"
                  onChange={() => changeShipping("pickup")}
                  name="flexRadioDefault"
                  id="flexRadioDefault1"
                />
                <label className="form-check-label" htmlFor="flexRadioDefault1">
                  Pickup
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  checked={cart.shipping.type === "ship"}
                  type="radio"
                  onChange={() => changeShipping("ship")}
                  name="flexRadioDefault"
                  id="flexRadioDefault2"
                />
                <label className="form-check-label" htmlFor="flexRadioDefault2">
                  Post It
                </label>
              </div>
            </div>
            {cart.shipping.type === "ship" && (
              <div id="shippingAddress" style={{ maxWidth: "600px" }}>
                <AddressElement options={shippingOptions} />
              </div>
            )}
          </td>
        </tr>
        <tr>
          <td colSpan={3} style={{ textAlign: "right" }}>
            Shipping
          </td>
          <td>${cart.shipping.cost}</td>
        </tr>
        <tr className="table-group-divider" style={{ fontSize: "140%" }}>
          <td colSpan={3} style={{ textAlign: "right" }}>
            Total
          </td>
          <td>${total}</td>
        </tr>
      </tfoot>
    </table>
  );
}

function CheckoutView({
  cart,
  total,
  onCheckoutSuccess,
  onChange,
  testMode,
}: {
  cart: Cart;
  total: number;
  testMode: boolean;
  onChange: (newCart: Cart) => void;
  onCheckoutSuccess: () => void;
}) {
  const stripe = useStripe()!;
  const elements = useElements();

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [buttonText, setButtonText] = useState<string>("Purchase");
  const [wasValidated, setWasValidated] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);

  function setError(msg: string | undefined) {
    setErrorMessage(msg);
    setButtonText("Purchase");
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(undefined);

    if (elements == null || !formRef.current) {
      return;
    }
    setButtonText("Processing");

    setWasValidated(true);
    if (!formRef.current.checkValidity()) {
      setError("Check required inputs");
      event.preventDefault();
      event.stopPropagation();
      return;
    } else {
      console.log("Form is valid");
    }

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      // Show error to your customer
      setError(submitError.message);
      return;
    }

    const shipping = (await elements.getElement("address")?.getValue())?.value;

    const { error: confirmationTokenError, confirmationToken } = await stripe.createConfirmationToken({
      elements,
      params: {
        payment_method_data: {
          billing_details: {
            email: cart.email,
          },
        },
        shipping,
        return_url: "https://localhost:3000/returns",
      },
    });

    if (confirmationTokenError) {
      setError(confirmationTokenError.message);
      return;
    }

    const form: CheckoutForm = {
      line_items: cart.items.map((item) => ({
        work_id: item.work.id,
        variant_name: item.variant.name,
        qty: item.qty,
      })),
      testMode,
      confirmationTokenId: confirmationToken.id,
      instructions: cart.instructions,
      total,
      dryRun: false,
    };

    try {
      // Create the PaymentIntent and obtain clientSecret from your server endpoint
      const response = await fetch("https://cqbcvlpcafzqezutoebw4ox7oi0jgzfa.lambda-url.ap-southeast-2.on.aws/checkout", {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify(form),
      });

      console.log("Response", response);

      const json = await response.json();

      if (!response.ok || json.error) {
        setError(json.error?.message ?? "An Error occurred");
        return;
      } else if (json.status === "requires_action") {
        // Use Stripe.js to handle the required next action
        const { error } = await stripe.handleNextAction({
          clientSecret: json.clientSecret,
        });

        if (error) {
          // Show error from Stripe.js in payment form
          setError(error.message);
        } else {
          // Actions handled, show success message
          setButtonText("Purchase");
        }
      } else {
        // No actions needed, show success message
        setButtonText("Success!");
        onCheckoutSuccess();
        // window.location.replace("/work/checkout_success");
      }
    } catch (ex: any) {
      console.log(ex);
      setError(ex.message);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={wasValidated ? "needs-validation was-validated" : "needs-validation"}
      noValidate
      ref={formRef}
    >
      <div className="mb-3">
        <label htmlFor="emailInput" className="form-label">
          Email (Required)
        </label>
        <div className="input-group has-validation">
          <span className="input-group-text" id="inputGroupPrepend">
            @
          </span>
          <input
            className="form-control"
            type="email"
            required
            id="emailInput"
            value={cart.email}
            onChange={(evt) => onChange({ ...cart, email: evt.target.value })}
          />
          <div className="invalid-feedback">Invalid Email address</div>
        </div>
      </div>

      <div className="mb-3">
        <label htmlFor="instructionsTA" className="form-label">
          Notes/Delivery Instructions (Optional)
        </label>
        <textarea
          className="form-control"
          id="instructionsTA"
          rows={5}
          value={cart.instructions}
          onChange={(evt) => onChange({ ...cart, instructions: evt.target.value })}
        ></textarea>
      </div>
      <h3>Payment</h3>

      <PaymentElement />
      {errorMessage && (
        <div className="alert alert-danger" style={{ margin: "1em 0" }}>
          {errorMessage}
        </div>
      )}
      <button
        className="btn btn-light"
        type="submit"
        disabled={!stripe || !elements || cart.items.length === 0 || total === 0}
        style={{ marginTop: "1em" }}
      >
        {buttonText}
      </button>
    </form>
  );
}

const testKey = "pk_test_51PzypaJh9Q0ssotsnUp8CEoU3B0dftY6FdIQgnrcZu2caKYvRZ8DcQz25f4zJh7o5L0h4SOtxAG7oM9OelGhVkpd00Ty9Pt2HX";
const liveKey = "pk_live_51PzypaJh9Q0ssotsq4ARjiHFzdkUEDhX7lVLJdzlPawC2pEKIR4ADhcaFVOlEwUKjWfwyanPNn5uCi5FEc1fbYQw00zmMku1eC";
const options: stripeJs.StripeElementsOptions = {
  mode: "payment",
  amount: 1099,
  currency: "aud",
  paymentMethodCreation: "manual",
  appearance: {
    theme: "stripe",
  },
};

export default function App() {
  const [cart, setCart] = useState<Cart>(getCart());
  const [succeeded, setSucceeded] = useState(false);
  const testMode = localStorage.getItem("test_mode") == "true";
  const stripePromise = useMemo(() => loadStripe(testMode ? testKey : liveKey), [testMode]);

  function updateCart(newCart: Cart) {
    setCart(newCart);
    saveCart(newCart);

    if (newCart.items.length === 0) {
      document.location.href = "/";
    }
  }

  function clearCart() {
    const newCart: Cart = { items: [], shipping: { type: "pickup", cost: 0 }, email: "", instructions: "" };
    setCart(newCart);
    saveCart(newCart);
  }

  const subTotal = cart.items.reduce((accum, li) => accum + li.qty * li.variant.price, 0);
  const total = subTotal + cart.shipping.cost;

  if (succeeded) {
    return (
      <div className="container-xl">
        <div className="alert alert-success" style={{ margin: "1em 0" }}>
          <h3>Thank you!</h3>
          <p>Your order has been placed. Melissa will be in touch with shipping/pickup details.</p>
          <a href="/">Back to the website</a>
        </div>
      </div>
    );
  }

  function onCheckoutSuccess() {
    clearCart();
    setSucceeded(true);
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <h1>
        Cart {testMode && <span>(TEST MODE)</span>}
        <button
          style={{ float: "right" }}
          onClick={() => {
            clearCart();
            document.location.href = "/";
          }}
          className="btn btn-light btn-sm"
        >
          {bagXIcon()}Clear
        </button>
      </h1>

      <CartView cart={cart} onChange={updateCart} />
      <CheckoutView cart={cart} total={total} onChange={updateCart} onCheckoutSuccess={onCheckoutSuccess} testMode={testMode} />
    </Elements>
  );
}
